import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LuppApi } from '../lupp.service';
import { LUPP_QUERY_KEYS } from '../constants.lupp';
import {
  Prospect,
  CreateProspectRequest,
  CreateProspectResponse,
  UpdateProspectRequest,
  UpdateProspectResponse,
  UpdateProspectStatusRequest,
  UpdateProspectStatusResponse,
  UseGetProspectsParams,
  ProspectDetailsResponse,
  ProspectMetricsResponse,
  UseGetProspectMetricsParams,
} from './dto.prospect';
// TODO: Remove mock data import once API is ready
import mockMetricsData from '../../../../data/prospectDesk/prospectMetrics.json';

/*
|--------------------------------------------------------------------------
| GET PROSPECTS
|--------------------------------------------------------------------------
*/

export const useGetProspects = ({
  page = 0,
  size = 25,
  searchString,
  sortField,
  sortOrder = 'desc',
  enabled = true,
}: UseGetProspectsParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PROSPECTS, page, size, searchString, sortField, sortOrder],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: String(page),
        size: String(size),
      });

      const body: {
        searchString?: string;
        sortField?: string;
        sortOrder?: string;
      } = {};
      if (searchString) body.searchString = searchString;
      if (sortField) body.sortField = sortField;
      if (sortOrder) body.sortOrder = sortOrder;

      const response = await LuppApi.post<
        {
          searchString?: string;
          sortField?: string;
          sortOrder?: string;
        },
        {
          data: Prospect[];
          totalCount: number;
          pageNumber: number;
          pageSize: number;
          currentPageCount: number;
          totalPages: number;
        }
      >(`sales/v1/prospects/searches?${queryParams.toString()}`, body, {
        extractData: (response) => response,
      });

      return {
        items: response.data,
        totalCount: response.totalCount,
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        currentPageCount: response.currentPageCount,
        totalPages: response.totalPages,
      };
    },
    placeholderData: (previousData) => previousData,
    enabled,
  });
};

/*
|--------------------------------------------------------------------------
| GET PROSPECT BY ID
|--------------------------------------------------------------------------
*/

export const useGetProspectById = (prospectId: string, enabled = true) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PROSPECTS, prospectId],
    queryFn: async () => {
      const response = await LuppApi.get<ProspectDetailsResponse>(
        `sales/v1/prospects/${prospectId}`
      );
      // Return full response with prospectDetails and quoteList
      return response;
    },
    enabled: enabled && !!prospectId,
  });
};

/*
|--------------------------------------------------------------------------
| CREATE PROSPECT
|--------------------------------------------------------------------------
*/

export const useCreateProspect = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProspectRequest) => {
      const response = await LuppApi.post<CreateProspectRequest, CreateProspectResponse>(
        'sales/v1/prospects',
        data
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch prospects list
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.PROSPECTS] });
    },
    onError: (error: Error) => {
      console.error('Failed to create prospect:', error.message);
    },
  });
};

/*
|--------------------------------------------------------------------------
| UPDATE PROSPECT
|--------------------------------------------------------------------------
*/

export const useUpdateProspect = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProspectRequest }) => {
      const response = await LuppApi.patch<UpdateProspectRequest, UpdateProspectResponse>(
        `sales/v1/prospects/${id}`,
        data
      );
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch prospects list and the specific prospect
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.PROSPECTS] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.PROSPECTS, variables.id] });
    },
    onError: (error: Error) => {
      console.error('Failed to update prospect:', error.message);
    },
  });
};

/*
|--------------------------------------------------------------------------
| UPDATE PROSPECT STATUS
|--------------------------------------------------------------------------
*/

export const useUpdateProspectStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProspectStatusRequest }) => {
      const response = await LuppApi.post<
        UpdateProspectStatusRequest,
        UpdateProspectStatusResponse
      >(`/sales/v1/prospects/status/${id}`, data);
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch prospects list and the specific prospect
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.PROSPECTS] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.PROSPECTS, variables.id] });
    },
    onError: (error: Error) => {
      console.error('Failed to update prospect status:', error.message);
    },
  });
};

/*
|--------------------------------------------------------------------------
| GET PROSPECT METRICS
|--------------------------------------------------------------------------
*/

export const useGetProspectMetrics = ({
  startDate,
  endDate,
  groupBy = 'month',
  enabled = true,
}: UseGetProspectMetricsParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PROSPECT_METRICS, startDate, endDate, groupBy],
    queryFn: async () => {
      // TODO: Uncomment once API is ready
      // const queryParams = new URLSearchParams();
      // if (startDate) queryParams.append('startDate', startDate);
      // if (endDate) queryParams.append('endDate', endDate);
      // if (groupBy) queryParams.append('groupBy', groupBy);
      //
      // const queryString = queryParams.toString();
      // const url = `sales/prospects/v1/prospects/metrics${queryString ? `?${queryString}` : ''}`;
      //
      // const response = await LuppApi.get<ProspectMetricsResponse>(url);
      // return response;

      // Mock data for development
      return mockMetricsData as ProspectMetricsResponse;
    },
    enabled,
  });
};
